import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { CuentaBeneficiario } from '../../interfaces/cuenta-beneficiario.interface';
import { CuentaRetiro } from '../../interfaces/cuenta-retiro.interface';
import { AccountRecipientService } from '../../services/account-recipient.service';
import {
  VitaBeneficiario,
  VitaBeneficiarioService,
} from '../../services/vita-beneficiario.service';
import { VitaCalculatorService } from '../../services/vita-calculator.service';
import { WithdrawAccountService } from '../../services/withdraw-account.service';
import {
  displayBanco,
  displayCuentaMask,
  displayNombre,
  flagAssetFor,
} from '../../utils/beneficiario-internacional.util';
import AddAccountComponent from '../account-recipient/components/add-account/add-account.component';
import { DialogBeneficiariosComponent } from '../account-recipient/components/account-list/dialog-beneficiarios/dialog-beneficiarios.component';
import { AddWithdrawAccountsComponent } from '../add-withdraw-accounts/add-withdraw-accounts.component';
import {
  DestinoRemesa,
  PerfilUsuario,
  TipoLibreta,
} from '../remittance-hub/models/remittance.model';
import * as R from '../remittance-hub/rules/remittance-rules';
import {
  SelectDestinoDialogComponent,
  SelectDestinoResult,
} from '../recipient-internacional/components/select-destino-dialog/select-destino-dialog.component';
import { AddVitaBeneficiarioDialogComponent } from '../vita-calculator-demo/components/add-vita-beneficiario-dialog/add-vita-beneficiario-dialog.component';
import { VitaBeneficiarioDetailDialogComponent } from '../vita-calculator-demo/components/vita-beneficiario-detail-dialog/vita-beneficiario-detail-dialog.component';

/** Un tab de la libreta = un país destino. */
export interface TabLibreta {
  pais: DestinoRemesa;
  nombre: string;
  moneda: string;
  flag: string | null;
  libreta: TipoLibreta;
}

/** Fila normalizada. Las tres libretas se pintan igual, pero no son iguales. */
export interface FilaBeneficiario {
  /** Id de la entidad original. Numérico en las propias, UUID en Vita. */
  id: string | number;
  libreta: TipoLibreta;
  pais: DestinoRemesa;
  titulo: string;
  /** Cuenta: completa en las libretas propias, enmascarada en Vita. */
  cuenta: string;
  banco: string | null;
  /**
   * Método de entrega. En Colombia NO es decorativo: Bre-B y transferencia
   * bancaria son variantes distintas de Vita y un beneficiario de una no sirve
   * para la otra.
   */
  metodo: string;
  bloqueado: boolean;
  motivoBloqueo: string | null;
  /** Texto sobre el que corre el buscador. */
  busqueda: string;
  raw: any;
}

/**
 * Lo que sabe hacer cada libreta. El shell no puede ofrecer un botón que la
 * libreta de abajo no tiene: la de Chile, por ejemplo, no tiene ficha.
 */
export interface CapacidadesLibreta {
  ficha: boolean;
  editar: boolean;
  eliminar: boolean;
  /** Publica estado del beneficiario (baneado, datos incompletos). Sólo Vita. */
  estado: boolean;
}

const CAPACIDADES: Record<TipoLibreta, CapacidadesLibreta> = {
  VENEZUELA: { ficha: true, editar: true, eliminar: true, estado: false },
  CHILE: { ficha: false, editar: true, eliminar: true, estado: false },
  VITA: { ficha: true, editar: true, eliminar: true, estado: true },
};

/**
 * La libreta del PROPIO país del perfil. No es un destino de remesa —nadie
 * manda una remesa a su propio país— pero sí tiene beneficiarios:
 *
 * - `VE`: el venezolano carga cuentas venezolanas para RETIRAR su saldo en Bs
 *   (`withdraw-balance-ves`, que justamente manda a esta pantalla cuando no
 *   tiene ninguna) y para mandar USDT→VES a una cuenta de Venezuela
 *   (`convert-usdt-ves`, destino `withdraw`).
 * - `CL`: queda afuera a propósito. La libreta de Chile (`CuentaRetiro`) para
 *   el chileno son sus CUENTAS PROPIAS de retiro, y ésas se administran en
 *   `/wallet/client/withdraw`; mostrarlas acá como "beneficiarios" sería
 *   duplicar esa pantalla con otro nombre. Vale también para el VES→CLP del
 *   chileno con saldo VES: sale a esas mismas cuentas propias (el cotizador
 *   las lista y permite agregar una), por eso `tabsDe` usa `origenesDe(perfil)`
 *   sin `conSaldoVes` y no suma el tab Chile.
 */
const LIBRETA_PROPIA: Record<PerfilUsuario, DestinoRemesa | null> = {
  VE: 'VE',
  CL: null,
};

/**
 * LIBRETA ÚNICA DE BENEFICIARIOS — resolución de las tres libretas.
 *
 * El cliente ve un tab por país al que le puede enviar; por debajo hay tres
 * entidades distintas, con tres servicios y tres formularios. Este servicio
 * normaliza el listado y despacha el alta, la edición y la baja a los diálogos
 * que YA existen: el de Vita es un motor de formularios dinámicos validado
 * contra el proveedor, y rehacerlos sería riesgo sin ganancia.
 *
 * NO toca backend: consume los mismos endpoints que las tres pantallas
 * vigentes, que quedan intactas.
 */
@Injectable({ providedIn: 'root' })
export class BeneficiaryBookService {
  constructor(
    private dialog: MatDialog,
    private accountRecipient: AccountRecipientService,
    private withdrawAccount: WithdrawAccountService,
    private vitaBeneficiario: VitaBeneficiarioService,
    private vitaCalculator: VitaCalculatorService,
  ) {}

  // ───────────────────── Tabs ─────────────────────

  /**
   * Tabs del perfil: los países a los que PUEDE enviar, más el suyo cuando ahí
   * también tiene beneficiarios (ver `LIBRETA_PROPIA`). El venezolano ve
   * Venezuela primero —es la libreta que usa para retirar sus Bs—; el chileno
   * sigue sin ver Chile.
   *
   * Los destinos salen de la misma matriz que gobierna el cotizador
   * (`rules/remittance-rules`), no de una lista propia: si el par deja de
   * existir, el tab desaparece solo. El país propio NO se agrega a esa matriz:
   * tener libreta no lo vuelve un destino de remesa.
   */
  tabsDe(perfil: PerfilUsuario): TabLibreta[] {
    const codigos = new Set<DestinoRemesa>();

    // Primero el propio: es el de uso más frecuente para ese perfil.
    const propio = LIBRETA_PROPIA[perfil];
    if (propio) codigos.add(propio);

    for (const origen of R.origenesDe(perfil)) {
      for (const d of R.destinosDe(perfil, origen)) codigos.add(d.code);
    }
    return Array.from(codigos).map((code) => {
      const meta = R.DESTINOS[code];
      return {
        pais: code,
        nombre: meta.nombre,
        moneda: meta.moneda,
        flag: flagAssetFor(code),
        libreta: meta.libreta,
      };
    });
  }

  libretaDe(pais: DestinoRemesa): TipoLibreta {
    return R.DESTINOS[pais].libreta;
  }

  capacidades(pais: DestinoRemesa): CapacidadesLibreta {
    return CAPACIDADES[this.libretaDe(pais)];
  }

  /** Países Vita activos hoy. Gatea el registro, no el listado. */
  async paisesVitaActivos(): Promise<Set<string>> {
    try {
      const paises = await firstValueFrom(this.vitaCalculator.listCountries());
      return new Set((paises || []).map((p) => (p.code || '').toUpperCase()));
    } catch {
      return new Set();
    }
  }

  // ───────────────────── Listar ─────────────────────

  async listar(
    pais: DestinoRemesa,
    usuarioId: number,
  ): Promise<FilaBeneficiario[]> {
    switch (this.libretaDe(pais)) {
      case 'VENEZUELA': {
        const lista = await this.accountRecipient.getByUser(usuarioId);
        return (lista || []).map((c) => this.filaVenezuela(c));
      }
      case 'CHILE': {
        const lista = await this.withdrawAccount.getWithdrawAccounts();
        // La libreta de retiro admite varios países: acá sólo va el del tab.
        return (lista || [])
          .filter((c) => (c.codigoPais || 'CL').toUpperCase() === pais)
          .map((c) => this.filaChile(c, pais));
      }
      case 'VITA': {
        const lista = await firstValueFrom(this.vitaBeneficiario.list(pais));
        return (lista || []).map((b) => this.filaVita(b, pais));
      }
      default:
        return [];
    }
  }

  // ───────────────────── Registro ─────────────────────

  /**
   * Abre el alta del país del tab. Devuelve `true` si se guardó.
   *
   * El país nunca se pregunta: lo fija el tab. Lo único que queda por elegir es
   * el MÉTODO, y eso cambia por libreta — Pago Móvil o cuenta en Venezuela,
   * banco y tipo de cuenta en Chile, variante de Vita en Colombia (Perú tiene
   * una sola y va directo al formulario).
   */
  async registrar(
    pais: DestinoRemesa,
    perfil: PerfilUsuario,
  ): Promise<boolean> {
    switch (this.libretaDe(pais)) {
      case 'VENEZUELA':
        return this.dialogoVenezuela({ type: 'CREATE', cuenta: null, perfil });
      case 'CHILE':
        return this.dialogoChile({ type: 'CREATE', cuenta: null, pais });
      case 'VITA':
        return this.altaVita(pais);
      default:
        return false;
    }
  }

  /**
   * Alta de Vita en dos pasos, con el país ya resuelto: el selector recibe UN
   * solo país, así que se salta ese paso y queda como selector de método.
   * Devuelve el schema del formulario ya cargado.
   */
  private async altaVita(pais: DestinoRemesa): Promise<boolean> {
    const activos = await firstValueFrom(this.vitaCalculator.listCountries());
    const country = (activos || []).find(
      (c) => (c.code || '').toUpperCase() === pais,
    );
    if (!country) return false;

    const picker = this.dialog.open(SelectDestinoDialogComponent, {
      data: { countries: [country] },
      panelClass: 'aguacate-dialog',
      ...this.tamano(520, 460),
    });

    const destino: SelectDestinoResult | null = await firstValueFrom(
      picker.afterClosed(),
    );
    if (!destino) return false;

    return this.dialogoVita({
      countryCode: destino.countryCode,
      variant: destino.variant,
      variantLabel: destino.variantLabel,
      schema: destino.schema,
    });
  }

  // ───────────────────── Edición ─────────────────────

  async editar(fila: FilaBeneficiario, perfil: PerfilUsuario): Promise<boolean> {
    switch (fila.libreta) {
      case 'VENEZUELA':
        return this.dialogoVenezuela({
          type: 'UPDATE',
          cuenta: fila.raw as CuentaBeneficiario,
          perfil,
        });
      case 'CHILE':
        return this.dialogoChile({
          type: 'UPDATE',
          cuenta: fila.raw as CuentaRetiro,
          pais: fila.pais,
        });
      case 'VITA': {
        const b = fila.raw as VitaBeneficiario;
        // Editar exige el schema de LA VARIANTE DEL BENEFICIARIO, no el del
        // país: es el formulario con el que se cargó.
        const schema = await firstValueFrom(
          this.vitaCalculator.getRules(b.variante),
        );
        return this.dialogoVita({
          countryCode: b.paisCodigo,
          variant: b.variante,
          variantLabel: b.varianteLabel || b.variante,
          schema,
          editId: b.id,
          initialValues: this.valoresIniciales(b),
        });
      }
      default:
        return false;
    }
  }

  // ───────────────────── Ficha ─────────────────────

  /** Abre la ficha en solo lectura. Chile no tiene ficha propia. */
  async verFicha(fila: FilaBeneficiario): Promise<void> {
    if (fila.libreta === 'VENEZUELA') {
      this.dialog.open(DialogBeneficiariosComponent, {
        data: fila.raw,
        disableClose: true,
        panelClass: 'aguacate-dialog',
        ...this.tamano(560, 500),
      });
      return;
    }

    if (fila.libreta !== 'VITA') return;

    const b = fila.raw as VitaBeneficiario;
    let aviso: string | null = fila.bloqueado ? fila.motivoBloqueo : null;

    if (!fila.bloqueado) {
      // Se avisa acá si le faltan datos, en vez de que se entere al enviar.
      try {
        const res = await firstValueFrom(this.vitaBeneficiario.completitud(b.id));
        if (!res.valid && (res.faltantes?.length || 0) > 0) {
          aviso = MSG_INCOMPLETO;
        }
      } catch {
        // Best-effort: la ficha se abre igual.
      }
    }

    this.dialog.open(VitaBeneficiarioDetailDialogComponent, {
      data: { beneficiario: b, hideUse: true, aviso },
      panelClass: 'aguacate-dialog',
      ...this.tamano(560, 480),
    });
  }

  // ───────────────────── Baja ─────────────────────

  /** Da de baja. La confirmación la pide el componente, con un texto único. */
  async eliminar(fila: FilaBeneficiario): Promise<void> {
    switch (fila.libreta) {
      case 'VENEZUELA':
        await this.accountRecipient.inactivateAccount(Number(fila.id));
        return;
      case 'CHILE':
        await this.withdrawAccount.inactivateAccount(Number(fila.id));
        return;
      case 'VITA':
        await firstValueFrom(this.vitaBeneficiario.archive(String(fila.id)));
        return;
    }
  }

  // ───────────────────── Diálogos ─────────────────────

  private async dialogoVenezuela(d: {
    type: 'CREATE' | 'UPDATE';
    cuenta: CuentaBeneficiario | null;
    perfil: PerfilUsuario;
  }): Promise<boolean> {
    const ref = this.dialog.open(AddAccountComponent, {
      data: {
        type: d.type,
        cuenta: d.cuenta,
        isVeUser: d.perfil === 'VE',
        // Sin monto en curso no hay restricción de Pago Móvil: esta pantalla
        // administra la libreta, no cotiza.
      },
      disableClose: true,
      panelClass: 'aguacate-dialog',
      ...this.tamano(620, 560),
    });
    return (await firstValueFrom(ref.afterClosed())) === true;
  }

  /**
   * Chile. `preselectedCountry` no es decorativo: sin él el diálogo navega a
   * `/wallet/client/withdraw` después de guardar
   * (`add-withdraw-accounts.component.ts:237`) y se lleva al usuario fuera de
   * la libreta.
   */
  private async dialogoChile(d: {
    type: 'CREATE' | 'UPDATE';
    cuenta: CuentaRetiro | null;
    pais: DestinoRemesa;
  }): Promise<boolean> {
    const ref = this.dialog.open(AddWithdrawAccountsComponent, {
      data: { type: d.type, cuenta: d.cuenta, preselectedCountry: d.pais },
      ...this.tamano(826, 800),
    });
    return (await firstValueFrom(ref.afterClosed())) === true;
  }

  private async dialogoVita(data: {
    countryCode: string;
    variant: string;
    variantLabel: string;
    schema: any;
    editId?: string;
    initialValues?: Record<string, any>;
  }): Promise<boolean> {
    const ref = this.dialog.open(AddVitaBeneficiarioDialogComponent, {
      data,
      disableClose: true,
      panelClass: 'aguacate-dialog',
      ...this.tamano(560, 500),
    });
    const creado: VitaBeneficiario | null = await firstValueFrom(
      ref.afterClosed(),
    );
    return !!creado;
  }

  // ───────────────────── Normalización ─────────────────────

  private filaVenezuela(c: CuentaBeneficiario): FilaBeneficiario {
    const titulo = `${c.nombre || ''} ${c.apellidos || ''}`.trim() || '—';
    const banco = c.banco?.nombre || null;
    const metodo =
      c.movilPay === 1 ? 'Pago Móvil' : c.tipoCuenta?.nombre || 'Cuenta bancaria';

    return {
      id: c.cuentaBeneficiarioID as number,
      libreta: 'VENEZUELA',
      pais: 'VE',
      titulo,
      cuenta: c.numeroCuenta || c.numeroID || '—',
      banco,
      metodo,
      bloqueado: false,
      motivoBloqueo: null,
      busqueda: `${titulo} ${c.numeroCuenta || ''} ${c.numeroID || ''} ${banco || ''} ${metodo}`.toLowerCase(),
      raw: c,
    };
  }

  private filaChile(c: CuentaRetiro, pais: DestinoRemesa): FilaBeneficiario {
    const titulo = c.alias || c.numeroCuenta || '—';
    return {
      id: c.id,
      libreta: 'CHILE',
      pais,
      titulo,
      cuenta: c.numeroCuenta || '—',
      banco: c.banco || null,
      metodo: c.tipoCuenta || 'Cuenta bancaria',
      bloqueado: false,
      motivoBloqueo: null,
      busqueda: `${titulo} ${c.numeroCuenta || ''} ${c.numeroId || ''} ${c.banco || ''} ${c.tipoCuenta || ''}`.toLowerCase(),
      raw: c,
    };
  }

  private filaVita(b: VitaBeneficiario, pais: DestinoRemesa): FilaBeneficiario {
    const baneado = b.vitaEstado === 'banned';
    const titulo = displayNombre(b);
    const banco = b.bancoNombre || null;
    const metodo = b.varianteLabel || (b.variante || '').toUpperCase();

    return {
      id: b.id,
      libreta: 'VITA',
      pais,
      titulo,
      cuenta: displayCuentaMask(b),
      banco: banco || displayBanco(b),
      metodo,
      bloqueado: baneado,
      motivoBloqueo: baneado ? MSG_BANEADO : null,
      busqueda: `${titulo} ${b.numeroId || ''} ${displayBanco(b)} ${b.email || ''} ${metodo}`.toLowerCase(),
      raw: b,
    };
  }

  /**
   * Valores del formulario de Vita: las columnas fijas más el JSON de datos
   * bancarios, con las mismas keys que publica el schema.
   */
  private valoresIniciales(b: VitaBeneficiario): Record<string, any> {
    const values: Record<string, any> = {
      beneficiary_first_name: b.nombre ?? '',
      beneficiary_last_name: b.apellidos ?? '',
      beneficiary_email: b.email ?? '',
      beneficiary_document_type: b.tipoId ?? '',
      beneficiary_document_number: b.numeroId ?? '',
      phone: b.telefono ?? '',
    };
    for (const k of Object.keys(b.datosBancarios || {})) {
      values[k] = b.datosBancarios[k];
    }
    return values;
  }

  private tamano(anchoMax: number, anchoMin: number) {
    const movil = typeof window !== 'undefined' && window.innerWidth < 768;
    return {
      maxWidth: movil ? '95vw' : `${anchoMax}px`,
      minWidth: movil ? '90vw' : `${anchoMin}px`,
      maxHeight: '90vh',
    };
  }
}

export const MSG_BANEADO =
  'Este beneficiario no está habilitado para recibir transferencias. Comunicate con un operador de Aguacate.';
export const MSG_INCOMPLETO =
  'Faltan datos de este beneficiario para poder enviarle. Comunicate con un operador de Aguacate para completarlos.';
